// Preferences engine — dot-path access, optimistic PATCH, live document effects.
import { useCallback, useEffect } from 'react';
import { api, Prefs, User } from './api';

// String defaults widen to `string` (not the literal) so callers can compare
// against other values without TS2367 "no overlap" errors.
export function getPref(prefs: Prefs | undefined, path: string, fallback: string): string;
export function getPref<T = any>(prefs: Prefs | undefined, path: string, fallback?: T): T;
export function getPref<T = any>(prefs: Prefs | undefined, path: string, fallback?: T): T {
  let cur: any = prefs;
  for (const k of path.split('.')) {
    if (cur == null) return fallback as T;
    cur = cur[k];
  }
  return (cur === undefined ? fallback : cur) as T;
}

function nest(path: string, value: any): Prefs {
  const keys = path.split('.');
  const out: any = {};
  let cur = out;
  keys.forEach((k, i) => {
    cur[k] = i === keys.length - 1 ? value : {};
    cur = cur[k];
  });
  return out;
}

/** Optimistic pref setter: patches the user locally, then persists. */
export function usePrefSetter(user: User | null, patchUser: (u: User) => void) {
  return useCallback(
    (path: string, value: any) => {
      if (!user) return;
      const optimistic: User = {
        ...user,
        prefs: deepMergeLocal(user.prefs || {}, nest(path, value)),
      };
      // keep users.theme mirrored for the ChatAuth theme effect
      if (path === 'appearance.color_mode' && (value === 'dark' || value === 'light')) {
        optimistic.theme = value;
      }
      if (path === 'appearance.color_mode' && value === 'system') {
        optimistic.theme = window.matchMedia('(prefers-color-scheme: light)').matches
          ? 'light'
          : 'dark';
      }
      patchUser(optimistic);
      api
        .updatePrefs(nest(path, value), optimistic.theme !== user.theme ? optimistic.theme : undefined)
        .then(patchUser)
        .catch(() => {});
    },
    [user, patchUser],
  );
}

function deepMergeLocal(base: Prefs, patch: Prefs): Prefs {
  const out: Prefs = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (v && typeof v === 'object' && !Array.isArray(v) && typeof out[k] === 'object' && !Array.isArray(out[k])) {
      out[k] = deepMergeLocal(out[k], v as Prefs);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** Applies document-level pref effects: zoom, motion, compact, links, named theme, system color mode. */
export function usePrefEffects(user: User | null, patchUser: (u: User) => void) {
  const prefs = user?.prefs;
  const zoom = getPref(prefs, 'accessibility.zoom', 100);
  const animations = getPref(prefs, 'accessibility.animations', true);
  const underline = getPref(prefs, 'accessibility.underline_links', false);
  const compact = getPref(prefs, 'appearance.compact', false);
  const themeName = getPref(prefs, 'appearance.theme_name', 'ochre');
  const colorMode = getPref(prefs, 'appearance.color_mode', 'dark');
  const dockTooltips = getPref(prefs, 'accessibility.dock_tooltips', true);

  useEffect(() => {
    _activeTz = getPref(prefs, 'language.auto_timezone', true) ? undefined : (getPref(prefs, 'language.timezone', '') || undefined);
    const root = document.documentElement;
    root.style.setProperty('--fx-zoom', String(zoom / 100));
    root.classList.toggle('fx-no-motion', !animations);
    root.classList.toggle('fx-compact', !!compact);
    root.classList.toggle('fx-underline-links', !!underline);
    root.classList.toggle('fx-no-tooltips', !dockTooltips);
    root.setAttribute('data-fx-theme', themeName);
  }, [zoom, animations, compact, underline, themeName, dockTooltips]);

  // System color mode: follow the OS and keep users.theme mirrored.
  useEffect(() => {
    if (colorMode !== 'system' || !user) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const apply = () => {
      const t = mq.matches ? 'light' : 'dark';
      if (user.theme !== t) {
        patchUser({ ...user, theme: t });
        api.updatePrefs({}, t).then(patchUser).catch(() => {});
      }
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [colorMode, user, patchUser]);
}

// ── Emoji skin tone ──

const SKIN_MOD = ['', '\u{1F3FB}', '\u{1F3FC}', '\u{1F3FD}', '\u{1F3FE}', '\u{1F3FF}'];
// Base emoji that accept a Fitzpatrick modifier (the common set we surface).
const SKINNABLE = new Set([
  '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '👏', '🙌', '🙏', '👋', '✋', '🖐️',
  '🤚', '👊', '✊', '💪', '☝️', '👆', '👇', '👉', '👈', '🤙', '🖖', '🫰', '🫶',
]);

export function applySkin(emoji: string, tone: number): string {
  const mod = SKIN_MOD[Math.max(0, Math.min(5, (tone || 1) - 1))];
  if (!mod) return emoji;
  const base = emoji.replace(/️$/, ''); // strip variation selector
  return SKINNABLE.has(emoji) || SKINNABLE.has(base) ? base + mod : emoji;
}

// ── Frequently-used emoji (local tally) ──

const FREQ_KEY = 'fxmchat_emoji_freq';
export function recordEmojiUse(emoji: string): void {
  try {
    const m = JSON.parse(localStorage.getItem(FREQ_KEY) || '{}');
    m[emoji] = (m[emoji] || 0) + 1;
    localStorage.setItem(FREQ_KEY, JSON.stringify(m));
  } catch {
    /* ignore */
  }
}
export function frequentEmoji(n: number): string[] {
  try {
    const m: Record<string, number> = JSON.parse(localStorage.getItem(FREQ_KEY) || '{}');
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, n).map(([e]) => e);
  } catch {
    return [];
  }
}

/** The one-click quick reactions to show, honoring source + skin tone. */
export function quickReactions(prefs: Prefs | undefined): string[] {
  const tone = getPref(prefs, 'appearance.emoji_skin_tone', 1);
  const custom = getPref<string[]>(prefs, 'messages.quick_reactions', ['✅', '👀', '🙌']) || [];
  let list = custom;
  if (getPref(prefs, 'messages.quick_reaction_source', 'custom') === 'frequent') {
    const freq = frequentEmoji(3);
    list = freq.length ? freq : custom;
  }
  return list.map((e) => applySkin(e, tone));
}

// ── Muting ──

export function isMuted(prefs: Prefs | undefined, id: string): boolean {
  return (getPref<string[]>(prefs, 'home.muted', []) || []).includes(id);
}

// ── Email-forwarding address (per-user, deterministic display) ──

export function forwardingAddress(userId: string | undefined): string {
  const hash = (userId || '').replace(/-/g, '').slice(0, 10) || 'inbox';
  return `fxm+${hash}@in.getfxm.ai`;
}

// ── Message-behavior helpers driven by prefs ──

const EMOTICONS: Record<string, string> = {
  ':)': '🙂', ':-)': '🙂', ':(': '🙁', ':-(': '🙁', ':D': '😄', ':-D': '😄',
  ';)': '😉', ';-)': '😉', ':P': '😛', ':-P': '😛', '<3': '❤️', ':o': '😮',
  ':O': '😮', ':|': '😐', ':/': '😕', 'xD': '😆', 'XD': '😆',
};

export function convertEmoticons(body: string): string {
  return body
    .split(/(\s+)/)
    .map((tok) => EMOTICONS[tok] ?? tok)
    .join('');
}

export function isJumbomoji(body: string): boolean {
  const t = body.trim();
  if (!t || t.length > 24) return false;
  const stripped = t.replace(/\p{Extended_Pictographic}|\p{Emoji_Modifier}|‍|️|\s/gu, '');
  return stripped === '' && /\p{Extended_Pictographic}/u.test(t);
}

export function hasOpenCodeFence(body: string): boolean {
  return ((body.match(/```/g) || []).length % 2) === 1;
}

export function fmtTime(iso: string | undefined, prefs: Prefs | undefined): string {
  if (!iso) return '';
  const auto = getPref(prefs, 'language.auto_timezone', true);
  const tz = getPref(prefs, 'language.timezone', '');
  const h24 = getPref(prefs, 'language.clock_24h', false);
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: !h24,
      ...(auto || !tz ? {} : { timeZone: tz }),
    });
  } catch {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}

export type ChannelLevel = '' | 'all' | 'mentions' | 'nothing';

/** Per-channel notification override, '' when the channel follows the global default. */
export function channelNotifyLevel(prefs: Prefs | undefined, channelId?: string): ChannelLevel {
  if (!channelId) return '';
  const v = getPref<string>(prefs, `notifications.channel_levels.${channelId}`, '');
  return v === 'all' || v === 'mentions' || v === 'nothing' ? v : '';
}

/** People (or agents) whose messages always break through mentions-only + muted conversations. */
export function isVip(prefs: Prefs | undefined, id?: string): boolean {
  if (!id) return false;
  return (getPref<string[]>(prefs, 'notifications.vip_people', []) || []).includes(id);
}

/**
 * Whether a notify event should alert. Precedence (most granular first):
 *   1. Do Not Disturb (manual pause or quiet-hours schedule) — silences everything.
 *   2. Per-channel level override (All / Mentions / Nothing) for this channel.
 *   3. Otherwise: the mute flag + the global level.
 * A @mention, a matched keyword (folded into `mentioned` by the caller), or a VIP
 * sender all "break through" a mentions-only level or a muted conversation — but never
 * DND or an explicit Nothing.
 */
export function shouldNotify(
  user: User | null,
  info: { isDm: boolean; mentioned: boolean; muted?: boolean; channelId?: string; senderId?: string },
): boolean {
  if (!user) return false;
  const p = user.prefs;
  const breakthrough = info.mentioned || isVip(p, info.senderId);

  // 1. Do Not Disturb — hard gate, nothing breaks through.
  if (user.notifications_paused) {
    const until = getPref<string | null>(p, 'notifications.paused_until', null);
    if (!until || new Date(until).getTime() > Date.now()) return false;
  }
  if (getPref(p, 'notifications.schedule_enabled', false)) {
    const now = new Date();
    const days = getPref(p, 'notifications.schedule_days', 'weekdays');
    const dow = now.getDay(); // 0 Sun
    if (days === 'weekdays' && (dow === 0 || dow === 6)) return false;
    const [sh, sm] = getPref(p, 'notifications.schedule_start', '09:00').split(':').map(Number);
    const [eh, em] = getPref(p, 'notifications.schedule_end', '18:00').split(':').map(Number);
    const mins = now.getHours() * 60 + now.getMinutes();
    if (mins < sh * 60 + sm || mins > eh * 60 + em) return false;
  }

  // 2. Per-channel override replaces the global level for this one channel.
  const level = channelNotifyLevel(p, info.channelId);
  if (level === 'nothing') return false;
  if (level === 'all') return true;
  if (level === 'mentions') return breakthrough;

  // 3. No per-channel override → mute flag + the global level.
  if (info.muted && !breakthrough) return false;
  const glob = getPref(p, 'notifications.notify_about', 'dms_mentions');
  if (glob === 'nothing') return false;
  if (glob === 'dms_mentions' && !info.isDm && !breakthrough) return false;
  return true;
}

let audioCtx: AudioContext | null = null;
function audio(): AudioContext | null {
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    audioCtx = audioCtx || new AC();
    // Browsers start the context suspended until a user gesture — resume so sound plays.
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
    // Honor the chosen sound-output device for synth tones too (newer Chromium).
    try {
      const sink = localStorage.getItem('fxmchat_sound_out') || '';
      const ctxAny = audioCtx as any;
      if (sink && typeof ctxAny.setSinkId === 'function' && ctxAny.sinkId !== sink) {
        ctxAny.setSinkId(sink).catch(() => { /* device gone — default */ });
      }
    } catch { /* ignore */ }
    return audioCtx;
  } catch {
    return null;
  }
}

/**
 * Unlock audio on the first user gesture so later notification sounds aren't muted by
 * the browser's autoplay policy. Call once at app mount; it self-removes.
 */
export function initAudioUnlock(): void {
  const unlock = () => {
    // Resume the WebAudio context INSIDE the gesture — privacy-hardened Chromium
    // forks (Ulaa, Brave…) disable media-engagement scoring, so a resume outside
    // a gesture never succeeds there.
    audio();
    // Element-level unlock: those same browsers gate each <audio> element on a
    // gesture. A muted play-then-pause inside the gesture blesses the element,
    // after which programmatic replays with sound are allowed.
    try {
      _notifEl = _notifEl || new Audio('/public/sound/notification.ogg');
      const el = _notifEl;
      el.muted = true;
      const pr = el.play();
      if (pr && typeof pr.then === 'function') {
        pr.then(() => { el.pause(); el.currentTime = 0; el.muted = false; })
          .catch(() => { el.muted = false; });
      } else {
        el.muted = false;
      }
    } catch { /* ignore */ }
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock, { once: false });
  window.addEventListener('keydown', unlock, { once: false });
}

function tone(
  c: AudioContext,
  freq: number,
  startAt: number,
  dur: number,
  peak = 0.09,
  type: OscillatorType = 'sine',
): void {
  const t0 = c.currentTime + startAt;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.015);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(c.destination);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}

// Real audio files are far more reliable than WebAudio oscillators across
// browsers (some Chromium forks gate the AudioContext hard) — the shipped
// sounds are primary, the synth tones are the fallback.
let _notifEl: HTMLAudioElement | null = null;

/** Output device for notification sounds. Defaults to the system default —
 *  which, with an HDMI monitor attached, is often the MONITOR (no speakers),
 *  so chimes "play" inaudibly. An explicit choice lives in localStorage
 *  (device ids are per-browser, so this never goes in account prefs). */
export const SOUND_OUT_KEY = 'fxmchat_sound_out';
export function soundSinkId(): string {
  try { return localStorage.getItem(SOUND_OUT_KEY) || ''; } catch { return ''; }
}
/** Route an element to the chosen output; no-op on default / unsupported. */
export function applySoundSink(el: HTMLAudioElement): void {
  const sink = soundSinkId();
  const anyEl = el as any;
  if (sink && typeof anyEl.setSinkId === 'function') {
    anyEl.setSinkId(sink).catch(() => { /* device unplugged — system default */ });
  }
}

/** Short chime for a new message / mention. */
export function playNotifySound(user: User | null): void {
  const p = user?.prefs;
  if (getPref(p, 'notifications.mute_all_sounds', false)) return;
  if (!getPref(p, 'notifications.sound_message', true)) return;
  try {
    _notifEl = _notifEl || new Audio('/public/sound/notification.ogg');
    applySoundSink(_notifEl);
    _notifEl.currentTime = 0;
    _notifEl.volume = 0.6;
    _notifEl.muted = false;
    const pr = _notifEl.play();
    if (pr && typeof pr.catch === 'function') {
      pr.catch(() => {
        // Element blocked — try the synth path, resuming the context first in
        // case it is still suspended (strict autoplay browsers).
        const c = audio();
        if (c && c.state === 'suspended') c.resume().then(() => _notifyTones()).catch(() => _notifyTones());
        else _notifyTones();
      });
    }
    return;
  } catch { /* fall through to tones */ }
  _notifyTones();
}

function _notifyTones(): void {
  const c = audio();
  if (!c) return;
  tone(c, 660, 0, 0.13);
  tone(c, 880, 0.1, 0.2);
}

/** Browser-tab attention flash: alternates the title until the tab is focused.
 *  (Web apps can't flash the OS window — this is the closest equivalent.) */
let _flashTimer: number | null = null;
export function flashTitle(user: User | null): void {
  const mode = getPref(user?.prefs, 'notifications.flash_window', 'when_hidden');
  if (mode === 'never') return;
  if (mode === 'when_hidden' && document.visibilityState === 'visible') return;
  if (_flashTimer) return;  // already flashing
  const original = document.title;
  let on = false;
  const tick = () => { on = !on; document.title = on ? '🔴 New message' : original; };
  const stop = () => {
    if (_flashTimer) { window.clearInterval(_flashTimer); _flashTimer = null; }
    document.title = original;
    window.removeEventListener('focus', stop);
    document.removeEventListener('visibilitychange', onVis);
  };
  const onVis = () => { if (document.visibilityState === 'visible') stop(); };
  tick();
  _flashTimer = window.setInterval(tick, 900);
  window.addEventListener('focus', stop);
  document.addEventListener('visibilitychange', onVis);
  window.setTimeout(stop, 12_000);
}

/** Ringback for the CALLER while waiting for someone to pick up — the classic
 *  double burst. Soft, and routed through the chosen sound-output device. */
export function playRingback(): void {
  const c = audio();
  if (!c) return;
  tone(c, 425, 0, 0.38, 0.06, 'sine');
  tone(c, 425, 0.55, 0.38, 0.06, 'sine');
}

/** Spoken notice when a callee declines: "<name> is currently not available for call". */
export function speakUnavailable(name: string): void {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(`${(name || 'They').trim()} is currently not available for call`);
    synth.speak(u);
  } catch { /* speech synthesis unsupported — silent */ }
}

/** A telephone-style ring burst for an incoming call (repeat on an interval while ringing). */
export function playCallSound(user: User | null): void {
  const p = user?.prefs;
  if (getPref(p, 'notifications.mute_all_sounds', false)) return;
  if (!getPref(p, 'notifications.sound_huddle', true)) return;
  const c = audio();
  if (!c) return;
  tone(c, 520, 0, 0.4, 0.11, 'triangle');
  tone(c, 660, 0.16, 0.42, 0.11, 'triangle');
}

/** Screen-reader label for a message, honoring accessibility.verbosity. */
export function messageAria(
  prefs: Prefs | undefined,
  m: {
    body?: string;
    edited_at?: string | null;
    created_at?: string;
    attachments?: { alt?: string; name?: string }[];
    reactions?: { count: number }[];
  },
  sender: string,
): string {
  const v = (key: string) => getPref(prefs, `accessibility.verbosity.${key}`, true);
  const parts: string[] = [sender, m.body || ''];
  if (v('edited') && m.edited_at) parts.push('edited');
  if (v('image_alt')) {
    (m.attachments || []).forEach((a) => a.alt && parts.push(`image: ${a.alt}`));
  }
  if (v('attachment_count') && (m.attachments || []).length) {
    parts.push(`${m.attachments!.length} attachment${m.attachments!.length > 1 ? 's' : ''}`);
  }
  if (v('reaction_count')) {
    const n = (m.reactions || []).reduce((s, r) => s + r.count, 0);
    if (n) parts.push(`${n} reaction${n > 1 ? 's' : ''}`);
  }
  if (v('link_count')) {
    const n = ((m.body || '').match(/https?:\/\/\S+/g) || []).length;
    if (n) parts.push(`${n} link${n > 1 ? 's' : ''}`);
  }
  if (v('datetime') && m.created_at) parts.push(fmtTime(m.created_at, prefs));
  return parts.filter(Boolean).join(', ');
}

// The manual-timezone pref, exposed module-wide so every time formatter (threads,
// incidents, calendar, snooze labels) can honor it — not just fmtTime.
let _activeTz: string | undefined;
export function activeTz(): string | undefined { return _activeTz; }

// Date-separator helpers (Teams/Slack style), keyed on the calendar day in the
// active timezone. Used by the main timeline and the thread panel.
export function dayKey(iso?: string | null): string | null {
  return iso ? new Date(iso).toLocaleDateString('en-CA', { timeZone: activeTz() }) : null;
}
export function dayLabel(iso: string): string {
  const tz = activeTz();
  const k = (x: Date) => x.toLocaleDateString('en-CA', { timeZone: tz });
  const d = new Date(iso), now = new Date();
  if (k(d) === k(now)) return 'Today';
  if (k(d) === k(new Date(now.getTime() - 86400000))) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: tz });
}

export function setTitleBadge(user: User | null, count: number): void {
  const show = getPref(user?.prefs, 'notifications.show_badge', true);
  const base = 'Helm';
  document.title = show && count > 0 ? `(${count > 9 ? '9+' : count}) ${base}` : base;
}
